import api from '../utils/api';

// DashboardAnalyticsService
// -----------------------------------------------------------------------
// Fans out per-mediator analytics fetches against scale-be
// (/mediator/analytics/:user_id) and aggregates the responses client-side
// into the shape Dashboard.jsx + Mediators.jsx expect. Mirrors the pattern
// the scale-portal mediator-analytics view consumes, but summed across
// every mediator assigned to the logged-in case manager.
//
// Used by:
//   - Dashboard.jsx          (KPI tiles for Cases / Cases Booked, plus
//                             Cases Over Time, Bookings Over Time, Top
//                             Mediators charts)
//   - Mediators.jsx          (Active mediators + Avg Cases per Mediator
//                             KPI tiles)
//
// Stays on cm-be (NOT migrated here):
//   - Upcoming Cases table + traffic-light dots (CM owns onboarding /
//     payment / brief progress derivation)
//   - Inquiry conversion chart + Inquiries KPI tile (scale-be's
//     inquiriesMonthly is empty by design — comment in
//     scalemediation-be/src/controller/mediatorController.js:898)
//   - Admin Fee KPI tile (scale-be's analytics has no per-mediator
//     admin-fee aggregate — see mediatorController.js comments)
//
// Parallel-fetch policy: Promise.allSettled so a single mediator's failure
// doesn't blank the entire dashboard. The aggregator skips failed rows.

const MONTH_LABELS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * Fetch the mediator UUID list that the logged-in case manager is
 * scoped to. We reuse cm-be's /mediators endpoint — it already returns
 * the userId UUID alongside the mediator profile, and the request is
 * scoped to the case manager via the existing auth middleware. Limit
 * 200 matches the FilterBar dropdown population call (Dashboard.jsx
 * line ~985) so we never have to make a second roundtrip.
 *
 * Returns: [{ userId, name, status, totalCases, lastCaseDate, ... }]
 */
export async function fetchAssignedMediators() {
    const res = await api.get('/mediators?limit=200');
    const list = (res?.data?.data || []).filter((m) => m.userId);
    return list;
}

/**
 * Fan out per-mediator analytics fetches against scale-be in parallel.
 * Returns the raw response array (one entry per mediator). Failed rows
 * are returned as null so the caller can skip them defensively without
 * losing positional indexing.
 */
export async function fetchAllMediatorAnalytics(userIds) {
    if (!Array.isArray(userIds) || userIds.length === 0) return [];
    const settled = await Promise.allSettled(
        userIds.map((uid) =>
            // Best-effort: a failure here (e.g. the analytics proxy returning 401)
            // must NOT log the user out — skipAuthRedirect keeps the session.
            api
                .get(`/scale-proxy/mediators/${uid}/analytics`, { skipAuthRedirect: true })
                .then((r) => r?.data?.data || null)
        )
    );
    return settled.map((s) => (s.status === 'fulfilled' ? s.value : null));
}

// ----------------------------------------------------------------------
// Aggregation helpers
// ----------------------------------------------------------------------

/**
 * Sum a numeric path across all mediator analytics rows. Defensive
 * against missing rows / fields — a null analytics row contributes 0.
 */
function sumPath(rows, getter) {
    let total = 0;
    for (const r of rows) {
        if (!r) continue;
        const v = getter(r);
        if (typeof v === 'number' && !Number.isNaN(v)) total += v;
    }
    return total;
}

/**
 * MoM change calculator that mirrors server/utils/dashboardMath.js.
 * When prev=0 and curr>0, returns isNew:true with the raw count so the
 * pill renders "+N new" instead of the meaningless "+100%".
 */
function computeChange(curr, prev) {
    const diff = curr - prev;
    if (prev === 0 && curr > 0) {
        return {
            value: curr,
            percent: null,
            direction: 'up',
            isNew: true,
        };
    }
    let percent;
    if (prev > 0) percent = Math.round((diff / prev) * 100);
    else if (curr < 0) percent = -100;
    else percent = 0;
    return {
        value: diff,
        percent,
        direction: diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat',
        isNew: false,
    };
}

/**
 * Build a 12-month bucket window using the "current month at position 8"
 * layout — same math as cm-be/utils/dashboardCharts.js so the FE chart
 * builders that read `buckets[].year` / `buckets[].monthIdx` get an
 * identical shape. currentMonthPosition=8 (1-based) → 7 months before
 * current, 4 months after.
 */
function buildBuckets(now = new Date(), months = 12, currentMonthPosition = 8) {
    const baseYear = now.getUTCFullYear();
    const baseMonth = now.getUTCMonth();
    const monthsBefore = Math.max(0, Math.min(months - 1, currentMonthPosition - 1));
    const buckets = [];
    for (let offset = -monthsBefore; offset < months - monthsBefore; offset++) {
        const monthIdx = baseMonth + offset;
        const year = baseYear + Math.floor(monthIdx / 12);
        const normIdx = ((monthIdx % 12) + 12) % 12;
        const start = new Date(Date.UTC(year, normIdx, 1));
        const end = new Date(Date.UTC(year, normIdx + 1, 1));
        buckets.push({
            year,
            monthIdx: normIdx,
            label: MONTH_LABELS[normIdx],
            startIso: start.toISOString(),
            endIso: end.toISOString(),
            bucketType: 'month',
        });
    }
    return buckets;
}

/**
 * Aggregate cases-by-month series across all mediators' `cases[]`. The
 * `dateField` controls which timestamp drives the bucket — 'created_at'
 * for Cases Over Time, 'created_at' for Bookings Over Time (but Bookings
 * only counts cases with mediation_date set).
 */
function aggregateMonthlySeries(rows, { dateField = 'created_at', requireMediationDate = false, now = new Date() } = {}) {
    const buckets = buildBuckets(now);
    const counts = buckets.map(() => 0);
    for (const r of rows) {
        if (!r || !Array.isArray(r.cases)) continue;
        for (const c of r.cases) {
            if (requireMediationDate && !c.mediation_date) continue;
            const iso = c[dateField];
            if (!iso) continue;
            for (let i = 0; i < buckets.length; i++) {
                if (iso >= buckets[i].startIso && iso < buckets[i].endIso) {
                    counts[i]++;
                    break;
                }
            }
        }
    }
    const peak = Math.max(0, ...counts);
    // "Current" bucket → the one whose (year, monthIdx) matches `now`.
    const nowYear = now.getUTCFullYear();
    const nowMonthIdx = now.getUTCMonth();
    const currentIdx = buckets.findIndex((b) => b.year === nowYear && b.monthIdx === nowMonthIdx);
    const current = currentIdx >= 0 ? buckets[currentIdx] : buckets[buckets.length - 1];
    const currentCount = current ? counts[buckets.indexOf(current)] || 0 : 0;
    return {
        labels: buckets.map((b) => b.label),
        buckets: buckets.map((b) => ({
            label: b.label,
            year: b.year,
            monthIdx: b.monthIdx,
            bucketType: b.bucketType,
            startIso: b.startIso,
            endIso: b.endIso,
        })),
        counts,
        percentages: counts.map((c) => (peak === 0 ? 0 : Math.round((c / peak) * 100))),
        peak,
        thisMonth: current
            ? {
                  year: current.year,
                  monthIdx: current.monthIdx,
                  label: current.label,
                  count: currentCount,
                  percentage: peak === 0 ? 0 : Math.round((currentCount / peak) * 100),
              }
            : null,
    };
}

/**
 * Build the Top Mediators chart payload (names, ids, counts, percentages)
 * directly from the per-mediator analytics rows. `ids` carries the user_id
 * UUID so the FE chart's onClick can navigate to the mediator detail page.
 */
function aggregateTopMediators(rows, limit = 10) {
    const ranked = rows
        .filter((r) => r && r.name && (r.analytics?.totalCases || 0) > 0)
        .map((r) => ({
            id: r.userId,
            name: r.name,
            count: r.analytics?.totalCases || 0,
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, limit);
    const peak = ranked[0]?.count || 0;
    return {
        names: ranked.map((r) => r.name),
        ids: ranked.map((r) => r.id),
        counts: ranked.map((r) => r.count),
        percentages: ranked.map((r) => (peak === 0 ? 0 : Math.round((r.count / peak) * 100))),
    };
}

// ----------------------------------------------------------------------
// Public aggregators consumed by Dashboard.jsx + Mediators.jsx
// ----------------------------------------------------------------------

/**
 * Dashboard KPI aggregate. Returns the same key paths the FE reads off
 * `stats` today, but only the keys scale-be owns. Callers should MERGE
 * this onto the cm-be /dashboard payload so cm-only fields (admin fee,
 * inquiries, upcoming cases) survive.
 */
export function aggregateDashboardStats(rows) {
    const valid = rows.filter(Boolean);
    const totalCases = sumPath(valid, (r) => r.analytics?.totalCases || 0);
    const activeCases = sumPath(valid, (r) => r.analytics?.activeCases || 0);
    const casesBookedTotal = sumPath(valid, (r) => r.casesBooked?.total || 0);
    const casesBookedThisMonth = sumPath(valid, (r) => r.casesBooked?.thisMonth || 0);
    const casesBookedLastMonth = sumPath(valid, (r) => r.casesBooked?.lastMonth || 0);
    // Cases MoM is derived per-mediator from `change.cases.value` (this -
    // last). Summing the value deltas gives us the aggregated diff; we
    // need to back out this/last counts to compute a sensible percentage.
    // Reconstruct: thisMonth = (lastMonth) + value. Since we don't have
    // raw counts, we estimate by summing values + assuming prev sum from
    // valid rows. Safer to bucket cases[].created_at ourselves.
    const now = new Date();
    const ymKey = (d) => `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    const thisYM = ymKey(now);
    const lastYM = ymKey(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)));
    let casesThisMonth = 0;
    let casesLastMonth = 0;
    let cmFeeTotal = 0;
    let cmFeeThisMonth = 0;
    let cmFeeLastMonth = 0;
    for (const r of valid) {
        if (!Array.isArray(r.cases)) continue;
        for (const c of r.cases) {
            // case_manager_fee_amount is pre-computed to dollars by the
            // scale-BE analytics endpoint (percentage fees already
            // converted against the case's billed rate). Just sum.
            const feeAmt = parseFloat(c.case_manager_fee_amount) || 0;
            if (feeAmt > 0) {
                cmFeeTotal += feeAmt;
            }
            if (!c.created_at) continue;
            const d = new Date(c.created_at);
            if (Number.isNaN(d.getTime())) continue;
            const k = ymKey(d);
            if (k === thisYM) {
                casesThisMonth++;
                if (feeAmt > 0) cmFeeThisMonth += feeAmt;
            } else if (k === lastYM) {
                casesLastMonth++;
                if (feeAmt > 0) cmFeeLastMonth += feeAmt;
            }
        }
    }
    const activeMediators = valid.filter((r) => (r.status || '').toLowerCase() === 'active').length;
    return {
        totalCases,
        activeCases,
        casesBooked: casesBookedTotal,
        activeMediators,
        cmFee: Math.round(cmFeeTotal),
        change: {
            totalCases: { ...computeChange(casesThisMonth, casesLastMonth), compareLabel: 'last month' },
            casesBooked: { ...computeChange(casesBookedThisMonth, casesBookedLastMonth), compareLabel: 'last month' },
            cmFee: { ...computeChange(Math.round(cmFeeThisMonth), Math.round(cmFeeLastMonth)), compareLabel: 'last month' },
        },
    };
}

/**
 * Dashboard charts aggregate. Produces casesOverTime + bookingsOverTime
 * + topMediators in the exact shape DashboardCharts.jsx already consumes
 * (labels/buckets/counts/percentages/thisMonth — mirrors
 * cm-be/utils/dashboardCharts.js output).
 *
 * Inquiry conversion intentionally NOT produced here — cm-be still owns
 * that chart.
 */
export function aggregateDashboardCharts(rows) {
    const valid = rows.filter(Boolean);
    return {
        casesOverTime: aggregateMonthlySeries(valid, { dateField: 'created_at' }),
        bookingsOverTime: aggregateMonthlySeries(valid, { dateField: 'created_at', requireMediationDate: true }),
        topMediators: aggregateTopMediators(valid),
    };
}

/**
 * Mediators page KPI aggregate. Active + avg-cases-per-active land here.
 * newThisMonth / lostThisMonth are NOT derivable from scale-be's analytics
 * (would need a mediators.created_at + status_changed_at history). The
 * caller should keep those two on cm-be's /mediators/stats.
 */
export function aggregateMediatorsStats(rows) {
    const valid = rows.filter(Boolean);
    const active = valid.filter((r) => (r.status || '').toLowerCase() === 'active').length;
    const totalCases = sumPath(valid, (r) => r.analytics?.totalCases || 0);
    const avgCasesPerActive = active > 0 ? Math.round((totalCases / active) * 10) / 10 : 0;
    return {
        active,
        avgCasesPerActive,
        totalCases,
    };
}
